import React, { FC, RefObject, useEffect, useState } from 'react';

import {
    Alert,
    AlertIcon,
    Box,
    BoxProps,
    Button,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    Heading,
    Input,
    Select,
    Spinner,
    Text,
    Textarea,
    VStack
} from '@chakra-ui/react';
import { Field, FieldProps, Formik } from 'formik';
import getConfig from 'next/config';
import { FormattedMessage, useIntl } from 'react-intl';

import { getUserAllowedSchedulesCompactList } from '~/components/EventSchedules/UserAllowedSchedules';
import { useMicrositeConfig } from '~/components/Site/provider/MicrositeConfigProvider';
import { useEventAccess, useFirebaseApp } from '~/lib/firebase';
import { localizedTime, shortDate } from '~/lib/localization/date';

const { publicRuntimeConfig } = getConfig();

interface ISupportForm {
    initialFocusRef?: RefObject<HTMLInputElement> | undefined;
}

const getSubjectOptions = () => {
    const intl = useIntl();

    const optionsMap = {
        generic: intl.formatMessage({
            id: `support.form.field_subject.option.generic`,
            defaultMessage: 'Generelle Frage'
        }),
        reg_auth: intl.formatMessage({
            id: `support.form.field_subject.option.reg_auth`,
            defaultMessage: 'Registrierung/Anmeldung'
        }),
        technical: intl.formatMessage({
            id: `support.form.field_subject.option.technical`,
            defaultMessage: 'Technisches Problem'
        }),
        my_attendance: intl.formatMessage({
            id: `support.form.field_subject.option.my_attendance`,
            defaultMessage: 'Meine Anwesenheiten'
        }),
        other: intl.formatMessage({
            id: `support.form.field_subject.option.other`,
            defaultMessage: 'Andere...'
        })
    } as const;

    return Object.keys(optionsMap).map((key) => {
        return optionsMap[key];
    });
};

const ThankYouMessage: FC<BoxProps> = (props) => {
    return (
        <Box {...props}>
            <Text>
                <FormattedMessage
                    id="support.form.submitted.success"
                    defaultMessage="Danke für Ihre Anfrage. Sie erhalten die Anwort per E-mail."
                />
            </Text>
        </Box>
    );
};

const SupportForm: FC<ISupportForm> = ({ initialFocusRef }) => {
    const { event } = useMicrositeConfig();
    const { user, userId, newUserData } = useFirebaseApp();
    const eventAccess = useEventAccess({ event });
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [messageSent, setMessageSent] = useState(false);
    const [initialized, setInitialized] = useState(false);
    const intl = useIntl();

    useEffect(() => {
        // We delay the form rendering, to allow time for loading the state data.
        const timeout = setTimeout(() => {
            setInitialized(true);
        }, 2000);

        return () => {
            clearTimeout(timeout);
        };
    });

    if (!initialized) {
        return (
            <Flex
                direction="row"
                justifyContent="center"
                alignItems="center"
                textAlign="center"
                minHeight="75vh"
            >
                <Spinner />
            </Flex>
        );
    }

    if (messageSent) {
        return <ThankYouMessage mb="20px" />;
    }

    const initialValues = {
        email: '',
        fullname: '',
        subject: '',
        message: ''
    };

    if (newUserData) {
        initialValues.email = newUserData.email || '';
        initialValues.fullname = newUserData.displayName || '';
    }

    if (!initialValues.fullname && newUserData?.firstName) {
        initialValues.fullname =
            newUserData.firstName + (newUserData.lastName ? ' ' + newUserData.lastName : '');
    }

    const getUserInfo = () => {
        try {
            const user_info: {
                user_id: string | undefined;
                last_sign_in_time: string | undefined;
                ub_id?: string;
            } = {
                user_id: userId,
                last_sign_in_time: user?.metadata.lastSignInTime
            };

            if (newUserData?.ubId) {
                user_info.ub_id = newUserData.ubId;
            }

            return user_info;
        } catch (err) {
            console.error(err);
        }

        return {};
    };

    const getEventInfo = () => {
        try {
            const page_title = document.querySelector('h1.chakra-heading')?.textContent || '';

            const event_info: {
                event_title?: string | undefined;
                event_id?: string;
                page_title?: string;

                event_access?: string;
                event_access_days?: string;
                event_access_other?: string;
                event_allowed_contexts?: string;
            } = event?.id
                ? {
                      event_title: event?.title,
                      event_id: event?.id
                  }
                : {
                      page_title
                  };

            const event_access_compact_list =
                event?.id && newUserData
                    ? getUserAllowedSchedulesCompactList({
                          event,
                          eventAccess,
                          context: ['livestream', 'ondemand', 'ovp', 'exhibition']
                      })
                    : null;

            if (typeof event_access_compact_list === 'string') {
                event_info.event_access = event_access_compact_list;
            } else if (
                typeof event_access_compact_list === 'object' &&
                event_access_compact_list !== null
            ) {
                event_info.event_access_days = event_access_compact_list.daysWithAccess
                    .map((es) => es.title)
                    .join(' // ');
                if (event_access_compact_list.itemsWithAccess.length) {
                    event_info.event_access_other = event_access_compact_list.itemsWithAccess
                        .map((es) => {
                            return `${es.title} (${shortDate(es.start_time)} • ${localizedTime(
                                es.start_time
                            )}-${localizedTime(es.end_time)})`;
                        })
                        .join(' // ');
                }

                event_info.event_allowed_contexts = eventAccess.getAllowedContexts().join(', ');
            }

            return event_info;
        } catch (err) {
            console.error(err);
        }

        return {};
    };

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    try {
                        const user_info = getUserInfo();
                        const event_info = getEventInfo();

                        const debug_info = {
                            ...user_info,
                            ...event_info,
                            page_href: location?.href,
                            user_agent: navigator?.userAgent || '',
                            device_vendor: navigator?.vendor || ''
                        };

                        setSubmitting(true);
                        const response = await fetch(
                            `${publicRuntimeConfig.basePath}/api/support/send-email`,
                            {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({ ...values, debug_info })
                            }
                        );

                        if (response.status === 200) {
                            setMessageSent(true);
                        } else {
                            setSubmitError(
                                intl.formatMessage({
                                    id: 'support.form.submitted.error',
                                    defaultMessage:
                                        'Ein Fehler ist aufgetreten. Bitte versuchen Sie erneut.'
                                })
                            );
                        }
                    } catch (e) {
                        const err = e as { msg: string };
                        console.error(err);
                        setSubmitError(err.msg);
                    }
                }}
            >
                {({ values, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
                    <form onSubmit={handleSubmit}>
                        <VStack align="stretch" spacing="4">
                            <Heading size="sm" as="h3" borderTop="1px solid" pt="1">
                                <FormattedMessage
                                    id="support.form.group_contact_data.heading"
                                    defaultMessage="Ihre Kontaktdaten"
                                />
                            </Heading>
                            <Box>
                                <FormControl isRequired id="email" width="full">
                                    <FormLabel>
                                        <FormattedMessage
                                            id="support.form.field_email.label"
                                            defaultMessage="E-Mail Adresse"
                                        />
                                    </FormLabel>
                                    <Input
                                        ref={initialFocusRef}
                                        type="email"
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.email}
                                        disabled={isSubmitting}
                                    />
                                    <FormHelperText>
                                        <FormattedMessage
                                            id="support.form.field_email.helper_text"
                                            defaultMessage="An diese Adresse werden Sie unsere Antwort bekommen."
                                        />
                                    </FormHelperText>
                                </FormControl>
                            </Box>
                            <Box>
                                <FormControl isRequired id="fullname" width="full">
                                    <FormLabel>
                                        <FormattedMessage
                                            id="support.form.field_fullname.label"
                                            defaultMessage="Name"
                                        />
                                    </FormLabel>
                                    <Input
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={values.fullname}
                                        disabled={isSubmitting}
                                    />
                                </FormControl>
                            </Box>
                            <Heading size="sm" as="h3" borderTop="1px solid" pt="1">
                                <FormattedMessage
                                    id="support.form.group_inquiry.heading"
                                    defaultMessage="Ihre Anfrage"
                                />
                            </Heading>
                            <Box>
                                <Field name="subject">
                                    {({ field }: FieldProps) => (
                                        <FormControl isRequired id="subject" width="full">
                                            <FormLabel>
                                                <FormattedMessage
                                                    id="support.form.field_subject.label"
                                                    defaultMessage="Betreff"
                                                />
                                            </FormLabel>
                                            <Select
                                                {...field}
                                                placeholder={intl.formatMessage({
                                                    id: 'support.form.field_subject.placeholder',
                                                    defaultMessage: 'Bitte auswählen...'
                                                })}
                                                variant="filled"
                                                disabled={isSubmitting}
                                            >
                                                {getSubjectOptions().map((opt) => {
                                                    return (
                                                        <option key={opt} value={opt}>
                                                            {opt}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        </FormControl>
                                    )}
                                </Field>
                            </Box>
                            <Box>
                                <Field name="message">
                                    {({ field }: FieldProps) => (
                                        <FormControl isRequired id="message" width="full">
                                            <Textarea
                                                {...field}
                                                size="sm"
                                                rows={5}
                                                placeholder={intl.formatMessage({
                                                    id: 'support.form.field_message.placeholder',
                                                    defaultMessage: 'Ihre Anfrage ...'
                                                })}
                                                autoComplete="off"
                                                disabled={isSubmitting}
                                            />
                                            <FormHelperText>
                                                <FormattedMessage
                                                    id="support.form.field_message.helper_text"
                                                    defaultMessage="Bitte seien Sie so ausführlich wie möglich."
                                                />
                                            </FormHelperText>
                                        </FormControl>
                                    )}
                                </Field>
                            </Box>
                            <Box>
                                <Alert status="info" bg="transparent">
                                    <AlertIcon />
                                    <FormattedMessage
                                        id="support.form.alert_box.top"
                                        defaultMessage="Sie erhalten die Antwort auf Ihre Support-Anfrage per E-mail."
                                    />
                                </Alert>
                            </Box>
                            <Box textAlign="center" color="red.500" fontSize="sm">
                                {submitError}
                            </Box>
                            <Box textAlign="center" pb="8">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    isLoading={isSubmitting}
                                    variant="solid"
                                    colorScheme="brand"
                                >
                                    <FormattedMessage
                                        id="support.form.submit_button.text"
                                        defaultMessage="Anfrage senden"
                                    />
                                </Button>
                            </Box>
                        </VStack>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default SupportForm;
